import { useEffect, useState } from "react";
import { AuthEvent, authEventEmitter } from "@/lib/authorization/auth-events";
import { pushNotificationService } from "@/lib/push-notifications";
import { useShouldShowPermissionDialog } from "@/lib/push-notifications/hooks/use-should-show-permission-dialog";
import { SimpleNotificationPermissionDialog } from "@/lib/push-notifications/permission/simple-notification-permission-dialog";

interface SimpleNotificationProviderProps {
  children: React.ReactNode;
}

/**
 * Clean, simple notification provider that:
 * 1. Handles push notification service lifecycle
 * 2. Shows permission dialog when appropriate
 * 3. Nothing more, nothing less
 */
export function NotificationLifecycleProvider({ children }: SimpleNotificationProviderProps) {
  const shouldShowDialog = useShouldShowPermissionDialog();
  const [dialogOpen, setDialogOpen] = useState(false);
  // Handle push notification service lifecycle
  useEffect(() => {
    const handleLogin = async () => {
      try {
        await pushNotificationService.onUserLogin();
      } catch (error) {
        console.error("Failed to handle push notifications on login:", error);
      }
    };

    const handleLogout = async () => {
      try {
        await pushNotificationService.onUserLogout();
      } catch (error) {
        console.error("Failed to handle push notifications on logout:", error);
      }
    };

    const handleAccountSwitch = async () => {
      try {
        await pushNotificationService.onAccountSwitch();
      } catch (error) {
        console.error("Failed to handle push notifications on account switch:", error);
      }
    };

    const unsubscribeLogin = authEventEmitter.on(AuthEvent.USER_LOGGED_IN, handleLogin);
    const unsubscribeLogout = authEventEmitter.on(AuthEvent.USER_LOGGED_OUT, handleLogout);
    const unsubscribeSwitch = authEventEmitter.on(AuthEvent.ACCOUNT_SWITCHED, handleAccountSwitch);

    return () => {
      unsubscribeLogin();
      unsubscribeLogout();
      unsubscribeSwitch();
    };
  }, []);

  // Show dialog when appropriate
  useEffect(() => {
    if (shouldShowDialog && !dialogOpen) {
      setDialogOpen(true);
    }
  }, [shouldShowDialog, dialogOpen]);

  return (
    <>
      {children}
      <SimpleNotificationPermissionDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
